/**
 * The **campaign definition** (PRD #837 slice 2): the ordered node graph the
 * solo campaign runs on, declared as pure content the way `classes/` declares
 * classes and `items/` declares itemization.
 *
 * A node is a dungeon or a camp. A dungeon node declares its map source, an
 * authored map id or a generator preset, plus a per-map objective, whether it
 * hides a waypoint, and per-tier tuning of monster level and material rung.
 * Act 2 is therefore authoring, not engineering: append nodes here and the
 * hub's exit resolution, objective tracking, and settlement follow.
 *
 * Nothing in this module reads a world, an entity, a session, or a socket.
 * A schema guard runs over every declared node at module load, so a malformed
 * node is an authoring failure the server refuses to boot with rather than a
 * silent runtime surprise.
 *
 * The objective layer sits **above** the exit invariant, never instead of it:
 * the exit stays usable at any clear percentage, and the objective is what
 * completes the node and settles durable progress on the Soulstone.
 */

import { CAMPAIGN_TIER_IDS, type CampaignTierId } from './soulstone';
import { arpgMonsterFamily, type ArpgMonsterFamilyId } from '../arpgPopulation';
import { ARPG_MATERIAL_TIER_COUNT } from './items/materials';

/**
 * The procedural layout families a generator preset may name. The Shame cave
 * family is the one that ships; the Despise themed kit (PRD #837 slice 5)
 * registers here when it lands.
 */
export const CAMPAIGN_GENERATOR_FAMILIES = ['shame'] as const;
export type CampaignGeneratorFamily =
  (typeof CAMPAIGN_GENERATOR_FAMILIES)[number];

/**
 * A generator preset: what a procedural dungeon node hands the existing
 * layout generator and population planner. Re-entering the node re-rolls a
 * fresh seed; the preset is the part that stays authored.
 */
export interface CampaignGeneratorPreset {
  /** The procedural layout family the generator runs. */
  generator: CampaignGeneratorFamily;
  /** The monster family the population planner dresses every pack in. */
  monsterFamily: ArpgMonsterFamilyId;
  /**
   * How many floors the node descends. Declared-only for now: campaign runs
   * generate a single floor until multi-floor campaign runs land (slice 5).
   */
  floorCount: number;
}

/** Where a dungeon node's map comes from: an authored extraction or a preset. */
export type CampaignMapSource =
  | { kind: 'authored'; mapId: string }
  | { kind: 'generator'; preset: CampaignGeneratorPreset };

export const CAMPAIGN_OBJECTIVE_KINDS = [
  'slay-guardians',
  'slay-boss',
] as const;
export type CampaignObjectiveKind = (typeof CAMPAIGN_OBJECTIVE_KINDS)[number];

/**
 * A per-map objective (PRD #837): what completes the node, never what opens
 * the door.
 *
 * `slay-guardians` rides the named-guardian-pack mechanism the guarded cache
 * already proves: the run designates guardian packs and the objective settles
 * when the last designated guardian falls. `slay-boss` settles on the death of
 * the monster wearing the declared boss archetype.
 */
export type CampaignObjective =
  | { kind: 'slay-guardians' }
  | { kind: 'slay-boss'; bossArchetypeId: string };

/** One difficulty tier's tuning for one dungeon node. */
export interface CampaignNodeTuning {
  /** The level the node's monsters are planned and scaled at. */
  monsterLevel: number;
  /** The node's rung on the material ladder: the `N` of the drop window. */
  materialTier: number;
}

/** A dungeon node: a map with an objective, tuning, and maybe a waypoint. */
export interface CampaignDungeonNode {
  kind: 'dungeon';
  id: string;
  /** The player-facing destination name. */
  name: string;
  mapSource: CampaignMapSource;
  objective: CampaignObjective;
  /** Whether the map hides a physical waypoint to find (slice 4). */
  waypoint: boolean;
  tuning: Record<CampaignTierId, CampaignNodeTuning>;
}

/**
 * A camp node: a mini hub between dungeons (slice 6). Reaching one is a
 * checkpoint, so a camp completes by arrival and needs no objective or tuning.
 */
export interface CampaignCampNode {
  kind: 'camp';
  id: string;
  name: string;
}

export type CampaignNode = CampaignDungeonNode | CampaignCampNode;

/**
 * The archetype id of the first boss (PRD #837 slice 5).
 *
 * Declared here so the graph, the objective tracker, and the boss archetype
 * declaration all key on one exported constant rather than three copies of a
 * string. The archetype itself is slice 5 content under `arpgMonsters/`; until
 * it registers, the Despise node's objective simply cannot be met.
 */
export const DESPISE_BOSS_ARCHETYPE_ID = 'despise-boss';

/**
 * The campaign, in traversal order. The first act: Britain Graveyard, the
 * roadside camp, and Despise level 1, whose boss completes the campaign.
 */
export const CAMPAIGN_NODES: readonly CampaignNode[] = [
  {
    kind: 'dungeon',
    id: 'brit-graveyard',
    name: 'Britain Graveyard',
    mapSource: { kind: 'authored', mapId: 'britain-graveyard' },
    objective: { kind: 'slay-guardians' },
    waypoint: true,
    tuning: {
      normal: { monsterLevel: 1, materialTier: 1 },
      nightmare: { monsterLevel: 20, materialTier: 4 },
      hell: { monsterLevel: 40, materialTier: 7 },
    },
  },
  {
    kind: 'camp',
    id: 'roadside-camp',
    name: 'Roadside Camp',
  },
  {
    kind: 'dungeon',
    id: 'despise-1',
    name: 'Despise Level 1',
    mapSource: {
      kind: 'generator',
      preset: { generator: 'shame', monsterFamily: 'ratmen', floorCount: 1 },
    },
    objective: { kind: 'slay-boss', bossArchetypeId: DESPISE_BOSS_ARCHETYPE_ID },
    waypoint: false,
    tuning: {
      normal: { monsterLevel: 3, materialTier: 2 },
      nightmare: { monsterLevel: 22, materialTier: 5 },
      hell: { monsterLevel: 42, materialTier: 8 },
    },
  },
];

/** One authoring problem found on one declared node. */
export interface CampaignNodeIssue {
  nodeId: string;
  issue: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function validateTuning(
  node: CampaignDungeonNode,
  issues: CampaignNodeIssue[],
): void {
  // Authoring input may be missing whole records, not just fields: the guard's
  // contract is an issue list, never a TypeError out of the guard itself.
  if (node.tuning === null || typeof node.tuning !== 'object') {
    issues.push({ nodeId: node.id, issue: 'missing tuning' });
    return;
  }
  let previous: CampaignNodeTuning | undefined;
  for (const tier of CAMPAIGN_TIER_IDS) {
    const tuning = node.tuning[tier] as CampaignNodeTuning | undefined;
    if (!tuning) {
      issues.push({ nodeId: node.id, issue: `missing ${tier} tuning` });
      continue;
    }
    if (!Number.isSafeInteger(tuning.monsterLevel) || tuning.monsterLevel < 1) {
      issues.push({
        nodeId: node.id,
        issue: `${tier} monster level must be a positive integer`,
      });
    }
    if (
      !Number.isSafeInteger(tuning.materialTier) ||
      tuning.materialTier < 1 ||
      tuning.materialTier > ARPG_MATERIAL_TIER_COUNT
    ) {
      issues.push({
        nodeId: node.id,
        issue: `${tier} material rung must sit on the ladder`,
      });
    }
    if (previous) {
      // A later tier must pay and threaten strictly more, which is the
      // authoring half of the tier-scaling proof (PRD #837 slice 7).
      if (tuning.monsterLevel <= previous.monsterLevel) {
        issues.push({
          nodeId: node.id,
          issue: `${tier} monster level must rise past the previous tier`,
        });
      }
      if (tuning.materialTier <= previous.materialTier) {
        issues.push({
          nodeId: node.id,
          issue: `${tier} material rung must rise past the previous tier`,
        });
      }
    }
    previous = tuning;
  }
}

function validateMapSource(
  node: CampaignDungeonNode,
  issues: CampaignNodeIssue[],
): void {
  const source = node.mapSource;
  if (source === null || typeof source !== 'object') {
    issues.push({ nodeId: node.id, issue: 'missing map source' });
    return;
  }
  if (source.kind === 'authored') {
    if (!isNonEmptyString(source.mapId)) {
      issues.push({ nodeId: node.id, issue: 'authored map id is empty' });
    }
    return;
  }
  const preset = source.preset;
  if (preset === null || typeof preset !== 'object') {
    issues.push({ nodeId: node.id, issue: 'missing generator preset' });
    return;
  }
  if (
    !CAMPAIGN_GENERATOR_FAMILIES.includes(
      preset.generator as CampaignGeneratorFamily,
    )
  ) {
    issues.push({
      nodeId: node.id,
      issue: `unknown generator family '${String(preset.generator)}'`,
    });
  }
  if (!arpgMonsterFamily(preset.monsterFamily)) {
    issues.push({
      nodeId: node.id,
      issue: `unknown monster family '${String(preset.monsterFamily)}'`,
    });
  }
  if (!Number.isSafeInteger(preset.floorCount) || preset.floorCount < 1) {
    issues.push({
      nodeId: node.id,
      issue: 'floor count must be a positive integer',
    });
  }
}

function validateObjective(
  node: CampaignDungeonNode,
  issues: CampaignNodeIssue[],
): void {
  const objective = node.objective;
  if (objective === null || typeof objective !== 'object') {
    issues.push({ nodeId: node.id, issue: 'missing objective' });
    return;
  }
  if (
    !CAMPAIGN_OBJECTIVE_KINDS.includes(objective.kind as CampaignObjectiveKind)
  ) {
    issues.push({
      nodeId: node.id,
      issue: `unknown objective kind '${String(objective.kind)}'`,
    });
    return;
  }
  if (
    objective.kind === 'slay-boss' &&
    !isNonEmptyString(objective.bossArchetypeId)
  ) {
    issues.push({ nodeId: node.id, issue: 'boss archetype id is empty' });
  }
}

/**
 * The schema guard: every authoring problem across a declared graph.
 *
 * Exposed as a pure function so a test can hold a malformed node against it;
 * the shipped graph is asserted through it at module load below.
 */
export function validateCampaignGraph(
  nodes: readonly CampaignNode[],
): CampaignNodeIssue[] {
  const issues: CampaignNodeIssue[] = [];
  const seen = new Set<string>();
  for (const node of nodes) {
    if (!isNonEmptyString(node.id)) {
      issues.push({ nodeId: String(node.id), issue: 'node id is empty' });
    } else if (seen.has(node.id)) {
      issues.push({ nodeId: node.id, issue: `duplicate node id '${node.id}'` });
    } else {
      seen.add(node.id);
    }
    if (!isNonEmptyString(node.name)) {
      issues.push({ nodeId: node.id, issue: 'node name is empty' });
    }
    if (node.kind === 'dungeon') {
      validateMapSource(node, issues);
      validateObjective(node, issues);
      if (typeof node.waypoint !== 'boolean') {
        issues.push({ nodeId: node.id, issue: 'waypoint must be declared' });
      }
      validateTuning(node, issues);
    }
  }
  return issues;
}

// A malformed node is an authoring failure at load (PRD #837): the module
// refuses to exist rather than shipping a campaign that cannot resolve.
{
  const issues = validateCampaignGraph(CAMPAIGN_NODES);
  if (issues.length > 0) {
    throw new Error(
      `campaign graph is malformed: ${issues
        .map((issue) => `${issue.nodeId}: ${issue.issue}`)
        .join('; ')}`,
    );
  }
}

/** Resolve one declared node by id; undeclared ids answer null, never default. */
export function campaignNode(id: unknown): CampaignNode | null {
  if (typeof id !== 'string') return null;
  return CAMPAIGN_NODES.find((node) => node.id === id) ?? null;
}

export function isCampaignNodeId(id: unknown): id is string {
  return campaignNode(id) !== null;
}

/** Resolve one declared dungeon node by id; camps and unknowns answer null. */
export function campaignDungeonNode(id: unknown): CampaignDungeonNode | null {
  const node = campaignNode(id);
  return node?.kind === 'dungeon' ? node : null;
}

/** One node's tuning at one difficulty tier. */
export function campaignNodeTuning(
  node: CampaignDungeonNode,
  tier: CampaignTierId,
): CampaignNodeTuning {
  return node.tuning[tier];
}

/**
 * The first node this progress has not completed, in graph order, or null once
 * everything settled. This is the hub exit's resolution seam (slice 3): the
 * far door leads here, while waypoints and checkpoints are re-entry choices.
 */
export function nextCampaignNode(
  completedNodeIds: readonly string[],
): CampaignNode | null {
  const completed = new Set(completedNodeIds);
  return CAMPAIGN_NODES.find((node) => !completed.has(node.id)) ?? null;
}

/**
 * Whether a tier's campaign is complete: the final node settled, whatever else
 * did. Completing the final node is what unlocks the next tier (slice 7).
 */
export function isCampaignComplete(
  completedNodeIds: readonly string[],
  nodes: readonly CampaignNode[] = CAMPAIGN_NODES,
): boolean {
  const last = nodes[nodes.length - 1];
  return last !== undefined && completedNodeIds.includes(last.id);
}
